/**
 * Framer Motion configuration and reusable animation variants
 *
 * This module provides a consistent animation system across the app,
 * inspired by Linear and Notion's smooth, responsive feel.
 */

import type { Transition, Variants } from 'framer-motion';

// =============================================================================
// SPRING CONFIGURATIONS
// =============================================================================

/** Snappy spring for interactive elements (buttons, toggles) */
export const springSnappy: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 30,
};

/** Smooth spring for content transitions */
export const springSmooth: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
};

/** Bouncy spring for success/celebration animations */
export const springBouncy: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 15,
};

/** Gentle spring for subtle movements */
export const springGentle: Transition = {
  type: 'spring',
  stiffness: 200,
  damping: 25,
};

// =============================================================================
// DURATION PRESETS
// =============================================================================

export const durations = {
  /** Ultra-fast for micro-interactions (75ms) */
  instant: 0.075,
  /** Fast for hover states (150ms) */
  fast: 0.15,
  /** Normal for most transitions (200ms) */
  normal: 0.2,
  /** Medium for content changes (300ms) */
  medium: 0.3,
  /** Slow for page transitions (400ms) */
  slow: 0.4,
  /** Extra slow for dramatic effects (600ms) */
  dramatic: 0.6,
} as const;

// =============================================================================
// EASING CURVES
// =============================================================================

export const easings = {
  /** Standard ease-out for exits */
  out: [0.22, 1, 0.36, 1],
  /** Ease-in-out for symmetric animations */
  inOut: [0.4, 0, 0.2, 1],
  /** Expo ease for smooth deceleration */
  outExpo: [0.16, 1, 0.3, 1],
  /** Back ease for slight overshoot */
  outBack: [0.34, 1.56, 0.64, 1],
} as const;

// =============================================================================
// PAGE TRANSITION VARIANTS
// =============================================================================

/** Fade and slide up for page entrances */
export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.medium,
      ease: easings.out,
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: {
      duration: durations.fast,
      ease: easings.out,
    },
  },
};

/** Fade only (no movement) for subtle transitions */
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: durations.normal },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast },
  },
};

/** Scale and fade for modal/dialog content */
export const scaleVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.96,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: springSmooth,
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: durations.fast },
  },
};

// =============================================================================
// LIST & STAGGER VARIANTS
// =============================================================================

/** Container that staggers children animations */
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
};

/** Fast stagger for shorter lists */
export const staggerContainerFast: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05,
    },
  },
};

/** Individual list item animation */
export const listItemVariants: Variants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.out,
    },
  },
  exit: {
    opacity: 0,
    x: -10,
    transition: {
      duration: durations.fast,
    },
  },
};

/** Horizontal slide for horizontal lists */
export const listItemHorizontalVariants: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: springSmooth,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: durations.fast },
  },
};

// =============================================================================
// INTERACTIVE ELEMENT VARIANTS
// =============================================================================

/** Card hover effect with lift and shadow */
export const cardHoverVariants: Variants = {
  initial: {
    y: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  },
  hover: {
    y: -2,
    boxShadow: '0 10px 30px rgba(0,0,0,0.08), 0 4px 10px rgba(0,0,0,0.04)',
    transition: springSnappy,
  },
  tap: {
    y: 0,
    boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
    transition: { duration: durations.instant },
  },
};

/** Button press effect */
export const buttonVariants: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.02 },
  tap: { scale: 0.98 },
};

/** Icon button (smaller scale range) */
export const iconButtonVariants: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.1 },
  tap: { scale: 0.95 },
};

// =============================================================================
// FEEDBACK & STATUS VARIANTS
// =============================================================================

/** Success checkmark animation */
export const successVariants: Variants = {
  initial: {
    scale: 0,
    rotate: -180,
  },
  animate: {
    scale: 1,
    rotate: 0,
    transition: springBouncy,
  },
};

/** Error shake animation */
export const errorShakeVariants: Variants = {
  initial: { x: 0 },
  animate: {
    x: [0, -10, 10, -10, 10, 0],
    transition: { duration: 0.5 },
  },
};

/** Pulse for attention-grabbing elements */
export const pulseVariants: Variants = {
  initial: { scale: 1, opacity: 1 },
  animate: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

/** Badge notification pop */
export const badgePopVariants: Variants = {
  initial: { scale: 0, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: springBouncy,
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: durations.fast },
  },
};

// =============================================================================
// TOAST & NOTIFICATION VARIANTS
// =============================================================================

/** Toast slide in from right */
export const toastVariants: Variants = {
  initial: {
    opacity: 0,
    x: 100,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: springSmooth,
  },
  exit: {
    opacity: 0,
    x: 100,
    scale: 0.95,
    transition: { duration: durations.normal },
  },
};

/** Toast slide in from top (for top-positioned toasts) */
export const toastTopVariants: Variants = {
  initial: {
    opacity: 0,
    y: -50,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: springSmooth,
  },
  exit: {
    opacity: 0,
    y: -50,
    scale: 0.95,
    transition: { duration: durations.normal },
  },
};

// =============================================================================
// OVERLAY & BACKDROP VARIANTS
// =============================================================================

/** Modal/dialog overlay fade */
export const overlayVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: durations.normal },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast },
  },
};

/** Dropdown/popover appearance */
export const dropdownVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: -4,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: springSnappy,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -4,
    transition: { duration: durations.fast },
  },
};

// =============================================================================
// SIDEBAR & NAVIGATION VARIANTS
// =============================================================================

/** Sidebar collapse/expand */
export const sidebarVariants: Variants = {
  expanded: {
    width: '16rem',
    transition: { duration: durations.medium, ease: easings.out },
  },
  collapsed: {
    width: '4rem',
    transition: { duration: durations.medium, ease: easings.out },
  },
};

/** Active nav indicator (for layoutId animations) */
export const navIndicatorTransition: Transition = {
  type: 'spring',
  stiffness: 500,
  damping: 35,
};

// =============================================================================
// SKELETON & LOADING VARIANTS
// =============================================================================

/** Shimmer effect for skeletons */
export const shimmerVariants: Variants = {
  initial: { x: '-100%' },
  animate: {
    x: '100%',
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

/** Spinner rotation */
export const spinnerVariants: Variants = {
  initial: { rotate: 0 },
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: 'linear',
    },
  },
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Creates a stagger container with custom timing
 */
export function createStaggerContainer(
  staggerChildren: number = 0.05,
  delayChildren: number = 0.1,
): Variants {
  return {
    initial: {},
    animate: {
      transition: {
        staggerChildren,
        delayChildren,
      },
    },
  };
}

/**
 * Creates slide variants with custom direction and distance
 */
export function createSlideVariants(
  direction: 'up' | 'down' | 'left' | 'right' = 'up',
  distance: number = 20,
): Variants {
  const isVertical = direction === 'up' || direction === 'down';
  const sign = direction === 'up' || direction === 'left' ? 1 : -1;
  const offset = distance * sign;
  const exitOffset = distance * sign * -0.5;

  if (isVertical) {
    return {
      initial: { opacity: 0, y: offset },
      animate: { opacity: 1, y: 0, transition: springSmooth },
      exit: { opacity: 0, y: exitOffset, transition: { duration: durations.fast } },
    };
  }

  return {
    initial: { opacity: 0, x: offset },
    animate: { opacity: 1, x: 0, transition: springSmooth },
    exit: { opacity: 0, x: exitOffset, transition: { duration: durations.fast } },
  };
}

/**
 * Checks if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  // Guard against matchMedia not being available or returning undefined (common in test environments)
  const mql =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  return mql?.matches ?? false;
}

/**
 * Returns animation props or empty object based on reduced motion preference
 */
export function getMotionProps<T extends object>(props: T): T | Record<string, never> {
  if (prefersReducedMotion()) return {};
  return props;
}
